import { Ellipsis, Select, SelectorLabel, Tooltip } from '@zoom/components';
import classnames from 'classnames';
import { TFunction } from 'i18next';
import { isEmpty } from 'ramda';
import React from 'react';
import { Field, FieldRenderProps } from 'react-final-form';
import { Label } from 'reactstrap';

import type { TValidator } from '../../utils/validations/types';

export type CustomLabelProps = {
  label: string;
  value: string;
};

export type CustomSelectionLabelProps = {
  value: string[];
};

const createSelectField =
  (
    t: TFunction,
    label: string,
    dataTestId: string,
    disabled: boolean,
    className?: string,
    multiple?: boolean,
    customLabelComponent?: ({ label, value }: CustomLabelProps) => React.ReactNode,
    customSelectionLabelComponent?: ({ value }: CustomSelectionLabelProps) => React.ReactNode,
    labelCount?: number,
    searchable = true,
    options: any[] = []
  ) =>
  ({ input, meta }: FieldRenderProps<string[]>) => {
    if (!options) {
      return null;
    }

    const hasValue = !isEmpty(input.value);

    const mappedOptions = options.map((item) => ({
      id: item!.id,
      key: item!.id,
      label: item.name,
      value: item!.id,
      labelComponent: customLabelComponent,
    }));

    const selection = input.value && !multiple ? options.find(({ id }) => id === input.value) : null;

    const value = Array.isArray(input.value)
      ? input.value.length
        ? input.value
        : undefined
      : input.value !== undefined && input.value !== ''
      ? [input.value]
      : undefined;

    const onSelectHandler = (updatedIndices: string[]) => {
      const updatedValue = multiple ? updatedIndices : updatedIndices.length ? updatedIndices[0] : undefined;

      input.onChange(updatedValue);
    };

    // Single value Label
    const singleSelectionNameLabel = selection && (
      <Tooltip message={selection.name} placement="bottom">
        {/* @ts-expect-error: Incompatible type of FC from React < v18 */}
        <Ellipsis>{selection.name}</Ellipsis>
      </Tooltip>
    );

    // Multiple values Label
    const multipleLabelText = t('filter.selected', { count: input.value.length });
    const multipleSelectionNameLabel = input.value.length ? (
      <Tooltip message={multipleLabelText} placement="bottom">
        {/* @ts-expect-error: Incompatible type of FC from React < v18 */}
        <Ellipsis>{multipleLabelText}</Ellipsis>
      </Tooltip>
    ) : null;

    const singleOrMultipleSelectionNameLabel = multiple ? multipleSelectionNameLabel : singleSelectionNameLabel;

    const labelContent = customSelectionLabelComponent
      ? customSelectionLabelComponent({ value: input.value })
      : singleOrMultipleSelectionNameLabel;

    const renderLabel = (
      <SelectorLabel label={<Label for={input.name}>{t(label, { count: labelCount })}</Label>}>
        {labelContent}
      </SelectorLabel>
    );

    const dropDownclassNames = classnames(
      'chart-selector',
      {
        selected: value || typeof input.value === 'number', // TODO: make Select work with all string|number|string[]|number[] value types
        'dropdown--invalid': meta.touched && meta.invalid && meta.error,
        'dropdown--valid': (!meta.pristine && meta.valid) || (hasValue && !meta.dirty),
      },
      className
    );

    return (
      <Select
        className={dropDownclassNames}
        data-test-id={dataTestId}
        disabled={disabled}
        key="select"
        label={renderLabel}
        multiple={multiple}
        onBlur={input.onBlur}
        onFocus={input.onFocus}
        onSelect={onSelectHandler}
        options={mappedOptions}
        resetLabel={t('filter.reset')}
        rest={[]}
        searchable={searchable}
        selectAllLabel={t('filter.all')}
        value={value}
      />
    );
  };

export interface SelectFieldProps {
  customLabelComponent?: ({ label, value }: CustomLabelProps) => React.ReactNode;
  customSelectionLabelComponent?: ({ value }: CustomSelectionLabelProps) => React.ReactNode;
  'data-test-id'?: string;
  options: any[];
  t: TFunction;
  name: string;
  label: string;
  labelCount?: number;
  validate?: TValidator;
  disabled?: boolean;
  className?: string;
  multiple?: boolean;
  searchable?: boolean;
}

export const SelectField: React.FC<SelectFieldProps> = ({
  customLabelComponent,
  customSelectionLabelComponent,
  'data-test-id': dataTestId = 'queue-dropdown',
  options = [],
  t,
  name,
  label,
  labelCount,
  validate,
  disabled,
  className,
  multiple = false,
  searchable,
}) => {
  const optionsState = !options.length;
  const disabledState = disabled !== undefined ? disabled : optionsState;

  return (
    <Field name={name} validate={validate}>
      {createSelectField(
        t,
        label,
        dataTestId,
        disabledState,
        className,
        multiple,
        customLabelComponent,
        customSelectionLabelComponent,
        labelCount,
        searchable,
        options || []
      )}
    </Field>
  );
};
